import { Box } from '@mui/material';
import {
  useCallback, useEffect, useReducer, useRef, useState,
} from 'react';
import ImageSettings, { Size } from '../../../../models/BlockComponentSettings/ImageSettings';
import BlockComponentImage from '../../../../models/BlockComponentValues/BlockComponentImage';
import { useGetBase64Image } from '../../../../hooks/UseGetBase64Image';
import { ImageOrientation } from '../../../Blocks/BlockImageList/ImageSettings';

interface ImagePreviewProps {
  image: BlockComponentImage;
  crop: Size;
  scale: number;
  handleChange: (settings: ImageSettings) => void;
  handleImageSizeChange: (size: Size) => void;
  moveable?: boolean;
}

const ImagePreview = ({
  image,
  scale,
  crop,
  handleChange,
  handleImageSizeChange,
  moveable = true,
}: ImagePreviewProps) => {
  const positionRef = useRef(image.settings.position);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const [selectedBorderRadius, setSelectedBorderRadius] = useState<number>(0);

  const base64Image = useGetBase64Image(image.value);

  const handleBorderRadiusChange = (borderRadius: string) => {
    if (!borderRadius) {
      return;
    }
    let borderRadiusValue = 0;

    // check if borderRadius is a number
    if (image.settings.orientation === ImageOrientation.Circle) {
      borderRadiusValue = 50;
    } else if (!Number.isNaN(Number(borderRadius))) {
      let ratio = 1;
      if (image.settings.orientation === ImageOrientation.Portrait) {
        ratio = 8;
      } else if (image.settings.orientation === ImageOrientation.Landscape) {
        ratio = 4;
      }
      borderRadiusValue = Number(borderRadius) / ratio;
    }

    setSelectedBorderRadius(borderRadiusValue);
  };

  const handleCrop = useCallback(() => {
    handleBorderRadiusChange(crop?.borderRadius);
    handleChange({
      ...image.settings,
      position: positionRef.current,
      size: {
        width: containerRef.current?.clientWidth,
        height: containerRef.current?.clientHeight,
        borderRadius: image?.settings?.size?.borderRadius,
      },
      frameSize: { width: crop?.width, height: crop?.height },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crop?.width, crop?.height, handleChange, image?.settings, crop?.borderRadius]);

  const handleMouseMove = (event: MouseEvent) => {
    event.preventDefault();
    positionRef.current = {
      x: positionRef.current.x + event.movementX,
      y: positionRef.current.y + event.movementY,
    };
    forceUpdate();
  };
  const handleMouseUp = (event: MouseEvent) => {
    event.preventDefault();
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    handleCrop();
  };

  useEffect(() => {
    handleCrop();
  }, [handleCrop]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '605px',
        height: '340px',
        display: 'flex',
        padding: `${(340 - (crop?.height || 300)) / 2}px`,
        position: 'relative',
        overflow: 'hidden',
        cursor: moveable ? 'move' : 'default',
      }}
      onMouseDown={(event) => {
        if (!moveable) return;

        event.preventDefault();
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
      }}
    >
      <Box sx={{
        width: crop?.width,
        height: crop?.height,
        margin: '0px auto',
        top: '50%',
        left: '50%',
      }}
      >
        <img
          id="image-preview"
          ref={imageRef}
          src={base64Image}
          alt="being edited"
          role="presentation"
          style={{
            objectFit: 'contain',
            width: '100%',
            height: '100%',
            transform: `translate(${positionRef.current.x}px, ${positionRef.current.y}px) scale(${scale})`,
          }}
          onLoad={() => {
            handleImageSizeChange({ width: imageRef.current?.naturalWidth, height: imageRef.current?.naturalHeight });
          }}
        />
      </Box>
      <Box
        id="crop-box"
        sx={{
          position: 'absolute',
          width: crop?.width,
          height: crop?.height,
          borderRadius: `${image.settings.orientation === ImageOrientation.Circle ? '50%' : `${selectedBorderRadius}px`}`,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, .5)',
          pointerEvents: 'none',
          WebkitBorderRadius: `${image.settings.orientation === ImageOrientation.Circle ? '50%' : `${selectedBorderRadius}px`}`,
        }}
      />
    </Box>
  );
};

export default ImagePreview;
